import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import DateField from './DateField';
// Every SpeciesAutocompleteService::HIGHER_TAXON_KEYS value offered as its
// own checkbox here, any subset combinable - 'insects' is deliberately
// left out (not deleted - see that service's own docblock): a narrower,
// pre-existing value other call sites (tag-based species assignment)
// already depend on, superseded here by the broader 'animals' (Kingdom
// Animalia) for what this exhibition-level setting was actually asked
// for. Order matches how the checkboxes render.
const SPECIES_GROUPS = ['fungi', 'myxo', 'plants', 'animals'];

/**
 * Creates a new exhibition, or edits an existing one - attrs.exhibition
 * (existing model) switches to edit mode; attrs.society (required on
 * create, a full Society model so its name can be shown) fixes which
 * société the exhibition belongs to - creation always happens from within
 * that société's own section on ExhibitionsListPage now, so there's no
 * picker to show, same "no picker, always fixed" shape as
 * CartothequeEntryEditModal/ToxicityEditModal's own attrs.society. Society
 * is immutable once set either way (see ExhibitionResource::fields()'s
 * groupId field - .writableOnCreate() server-side).
 */
export default class ExhibitionEditModal extends Modal {
  name = '';
  // ISO, '' for none, null while what's typed in its field isn't a date
  // (see DateField) - which blocks saving rather than dropping it.
  startDate = '';
  endDate = '';
  location = '';
  description = '';
  speciesGroups = (() => new Set())();
  exportToxicity = false;
  groupId = 0;
  loading = false;
  errorMessage = null;
  oninit(vnode) {
    super.oninit(vnode);
    const exhibition = this.attrs.exhibition;
    this.name = exhibition && exhibition.name() || '';
    this.startDate = exhibition && exhibition.startDate() || '';
    this.endDate = exhibition && exhibition.endDate() || '';
    this.location = exhibition && exhibition.location() || '';
    this.description = exhibition && exhibition.description() || '';
    // A Set, not the raw comma-separated string this ultimately reads
    // from/writes back to (see submit()) - checkbox state is naturally
    // "is this one on", membership-testing a Set for each of the fixed
    // SPECIES_GROUPS above is simpler than repeatedly parsing/rebuilding
    // a string mid-edit. Defaults to fungi+myxo for a brand new
    // exhibition, matching the column's own DB default for one created
    // any other way (see that migration's own docblock) - not read from
    // attrs.society, this is a per-exhibition setting, not a société one.
    this.speciesGroups = new Set(exhibition ? exhibition.speciesGroups().split(',').filter(Boolean) : ['fungi', 'myxo']);
    this.exportToxicity = !!(exhibition && exhibition.exportToxicity());
    this.groupId = exhibition ? exhibition.groupId() : this.attrs.society.groupId();
    this.loading = false;
    this.errorMessage = null;
  }
  className() {
    return 'Modal--medium ChampisExhibitionEditModal';
  }
  title() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.edit_modal.${key}`, params);
    };
    return this.attrs.exhibition ? t('edit_title') : t('create_title', {
      society: this.attrs.society.name()
    });
  }
  content() {
    const t = key => app.translator.trans(`champis-exhibitions.forum.edit_modal.${key}`);
    return m("div", {
      className: "Modal-body"
    }, this.errorMessage && m("div", {
      className: "ChampisExhibitionEditModal-error"
    }, this.errorMessage), m("div", {
      className: "ChampisExhibitionEditModal-field"
    }, m("label", null, t('name_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('name_placeholder'),
      value: this.name,
      oninput: e => {
        this.name = e.target.value;
      }
    }))), m("div", {
      className: "ChampisExhibitionEditModal-field"
    }, m("label", null, t('start_date_label'), m(DateField, {
      value: this.startDate || '',
      onchange: value => {
        this.startDate = value;
      }
    }))), m("div", {
      className: "ChampisExhibitionEditModal-field"
    }, m("label", null, t('end_date_label'), m(DateField, {
      value: this.endDate || '',
      onchange: value => {
        this.endDate = value;
      }
    }))), m("div", {
      className: "ChampisExhibitionEditModal-field"
    }, m("label", null, t('location_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('location_placeholder'),
      value: this.location,
      oninput: e => {
        this.location = e.target.value;
      }
    }))), m("div", {
      className: "ChampisExhibitionEditModal-field"
    }, m("label", null, t('description_label'), m("textarea", {
      className: "FormControl",
      placeholder: t('description_placeholder'),
      value: this.description,
      oninput: e => {
        this.description = e.target.value;
      }
    }))), m("div", {
      className: "ChampisExhibitionEditModal-field"
    }, m("label", null, t('species_groups_label')), m("div", {
      className: "ChampisExhibitionEditModal-speciesGroups"
    }, SPECIES_GROUPS.map(group => m("label", {
      key: group
    }, m("input", {
      type: "checkbox",
      checked: this.speciesGroups.has(group),
      onchange: e => {
        if (e.target.checked) {
          this.speciesGroups.add(group);
        } else {
          this.speciesGroups.delete(group);
        }
      }
    }), ' ', t(`species_group_${group}`)))), m("p", {
      className: "helpText"
    }, t('species_groups_hint'))), m("div", {
      className: "ChampisExhibitionEditModal-field"
    }, m("label", null, t('exports_label')), m("label", {
      className: "ChampisExhibitionEditModal-checkbox"
    }, m("input", {
      type: "checkbox",
      checked: this.exportToxicity,
      onchange: e => {
        this.exportToxicity = e.target.checked;
      }
    }), ' ', t('export_toxicity_label')), m("p", {
      className: "helpText"
    }, t('export_toxicity_hint'))), m("div", {
      className: "ChampisExhibitionEditModal-footer"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.submit(),
      loading: this.loading,
      disabled: !this.canSubmit()
    }, t('save_button'))));
  }
  canSubmit() {
    if (this.loading) return false;
    return !!this.name.trim() && !!this.startDate && this.endDate !== null && !!this.groupId;
  }
  submit() {
    if (this.endDate && this.startDate && this.endDate < this.startDate) {
      this.errorMessage = app.translator.trans('champis-exhibitions.forum.edit_modal.error_end_before_start');
      return;
    }
    this.loading = true;
    this.errorMessage = null;
    m.redraw();
    const attributes = {
      name: this.name.trim(),
      startDate: this.startDate,
      endDate: this.endDate || null,
      location: this.location.trim() || null,
      description: this.description.trim() || null,
      // Fixed SPECIES_GROUPS order regardless of click order, so the
      // stored value doesn't churn on every save - not that it matters
      // functionally (order is irrelevant to resolveHigherTaxonQuery()),
      // just tidier to look at directly in the database.
      speciesGroups: SPECIES_GROUPS.filter(group => this.speciesGroups.has(group)).join(','),
      exportToxicity: this.exportToxicity
    };

    // groupId only on create - Model.save() builds its request body from
    // exactly what's passed to *this* call (confirmed by reading
    // Model.ts's save() directly: `const data = { type: this.data.type,
    // attributes }`, no merge with whatever createRecord() may have been
    // seeded with earlier), and it's immutable after creation anyway
    // (server-side .writableOnCreate() on this field).
    if (!this.attrs.exhibition) {
      attributes.groupId = this.groupId;
    }
    const exhibition = this.attrs.exhibition || app.store.createRecord('champis-exhibitions');
    exhibition.save(attributes).then(() => {
      this.loading = false;
      this.hide();
      if (this.attrs.onsaved) this.attrs.onsaved(exhibition);
    }).catch(error => {
      this.loading = false;
      this.errorMessage = error && error.message || app.translator.trans('champis-exhibitions.forum.edit_modal.error');
      m.redraw();
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/ExhibitionEditModal', ExhibitionEditModal);